export interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  /** Human-friendly display name (from AAD claims via /api/me); falls back to userDetails. */
  displayName?: string;
}

const FALLBACK_ADMIN_EMAILS = ["bfietsam@brinkmannconstructors.com"];
const FALLBACK_SUPER_ADMIN_EMAILS = ["bfietsam@brinkmannconstructors.com"];

export async function getUser(): Promise<ClientPrincipal | null> {
  try {
    const res = await fetch("/.auth/me");
    if (!res.ok) return null;
    const data = await res.json();
    const principal: ClientPrincipal | null = data.clientPrincipal ?? null;
    if (!principal) return null;
    // Merge live admin/superAdmin status from /api/me so that newly-granted
    // admins don't have to sign out and back in to see the Admin button.
    try {
      const meRes = await fetch("/api/me");
      if (meRes.ok) {
        const me = (await meRes.json()) as { admin?: boolean; superAdmin?: boolean; displayName?: string };
        const roles = new Set(principal.userRoles ?? []);
        if (me.admin) roles.add("admin");
        if (me.superAdmin) {
          roles.add("admin");
          roles.add("superadmin");
        }
        principal.userRoles = Array.from(roles);
        if (me.displayName && me.displayName.trim()) principal.displayName = me.displayName.trim();
      }
    } catch { /* ignore — fall back to cookie roles */ }
    return principal;
  } catch {
    return null;
  }
}

/** Start an SWA AAD login when the current page requires an identity. */
export function signInForCurrentPage(): void {
  const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.assign(`/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(redirect)}`);
}

export function isAdmin(u: ClientPrincipal | null): boolean {
  if (!u) return false;
  if (u.userRoles?.includes("admin") || u.userRoles?.includes("superadmin")) return true;
  return FALLBACK_ADMIN_EMAILS.includes((u.userDetails ?? "").toLowerCase());
}

export function isSuperAdmin(u: ClientPrincipal | null): boolean {
  if (!u) return false;
  if (u.userRoles?.includes("superadmin")) return true;
  return FALLBACK_SUPER_ADMIN_EMAILS.includes((u.userDetails ?? "").toLowerCase());
}
